'use client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { t } from 'i18next'
import Modal from 'app/app/(client)/components/organisms/Modal/Modal'
import Spinner from 'app/app/(client)/components/atoms/Spinner/Spinner'
import SearchBox from 'app/app/(client)/components/molecules/SearchBox/SearchBox'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import useDebounce from '../../../hooks/useDebounce'
import { Constants } from '../../../utils/client-constants'
import { getSearchOpportunityData } from 'app/services/client/client-api'
import { importOpportunities } from 'app/app/(client)/utils/opportunity-utils'
import { useAppDispatch } from 'app/app/(client)/hooks/redux-hook'
import { hideAlert, showAlert } from 'app/app/(client)/redux/features/notification/alert-slice'
import { capitalizeIfEnglish } from 'app/app/(client)/utils/utils'
import { handleUIError } from 'app/services/client/error-handling'
import OpportunityImportList, { SearchResult } from './OpportunityImportList'
import { useNetwork } from 'app/app/(client)/hooks/useNetwork'

interface DialogProps {
  modalOpen: boolean
  setModalOpen: (open: boolean) => void
  sharedOpportunitiesCount: number
}
interface DialogState {
  pristine: boolean
  loading: boolean
  searchResults: SearchResult[]
}

const OpportunityImportModal: React.FC<DialogProps> = ({ modalOpen, setModalOpen, sharedOpportunitiesCount }) => {
  const dispatch = useAppDispatch()
  const [dialogState, setDialogState] = useState<DialogState>({
    pristine: true,
    loading: false,
    searchResults: []
  })
  const [searchQuery, setSearchQuery] = useState('')
  const isOnline = useNetwork()
  const { pristine, loading, searchResults } = dialogState
  const debouncedSearchTerm = useDebounce(searchQuery, Constants.DEBOUNCE_DELAY)
  const [opportunityIds, setOpportunityIds] = useState<string[]>([])
  const [buttonLoading, setButtonLoading] = useState(false)

  const allSelected = useMemo(
    () => searchResults.length > 0 && searchResults.every(r => opportunityIds.includes(r.id)),
    [searchResults, opportunityIds]
  )

  const toggleAll = useCallback(
    () => setOpportunityIds(allSelected ? [] : searchResults.map(r => r.id)),
    [allSelected, searchResults]
  )

  const toggleRow = useCallback(
    (rowId: string) =>
      setOpportunityIds(prev => (prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId])),
    []
  )

  const handleSearchQuery = useCallback(
    async (value: string, signal: AbortSignal) => {
      if (!isOnline) {
        return
      }

      setDialogState(prev => ({
        ...prev,
        pristine: false,
        loading: true
      }))

      try {
        const opportunityShareSearchResult = await getSearchOpportunityData(value, signal)

        const results = opportunityShareSearchResult.map(
          ({ id, customerName, opportunityName, owner, opportunityNumber }) => ({
            id,
            customerName,
            opportunityName,
            owner,
            opportunityNumber
          })
        )
        setDialogState(prev => ({
          ...prev,
          loading: false,
          searchResults: results
        }))
      } catch (error: any) {
        if (signal.aborted) {
          return
        }
        handleUIError(dispatch, error)
        setDialogState({
          pristine: true,
          loading: false,
          searchResults: []
        })
      }
    },
    [isOnline, dispatch]
  )

  const onImportOpportunities = useCallback(
    (oppIds: string[]) => {
      setButtonLoading(true)
      if (sharedOpportunitiesCount + oppIds.length > 100) {
        dispatch(
          showAlert({
            id: 'opportunity-count-warning-for-import-action',
            title: '',
            description: t('customers.sharedOpportunitiesCountWarning'),
            type: 'warning',
            onPrimaryClick: () => dispatch(hideAlert())
          })
        )
        setButtonLoading(false)
      } else {
        importOpportunities(dispatch, oppIds, true)
      }
    },
    [sharedOpportunitiesCount, dispatch]
  )

  const closeModal = useCallback(() => {
    setSearchQuery('')
    setDialogState({
      pristine: true,
      loading: false,
      searchResults: []
    })
    setModalOpen(false)
  }, [setModalOpen])

  const someSelected = useMemo(() => opportunityIds.length > 0, [opportunityIds])

  const handlePrimaryAction = useCallback(
    () => onImportOpportunities(opportunityIds),
    [onImportOpportunities, opportunityIds]
  )

  const thirdBtnContent = useMemo(
    () => (
      <Text className="text-[14px] font-semibold" as="div">
        {allSelected
          ? capitalizeIfEnglish(t('customers.refresh.deselectAll'))
          : capitalizeIfEnglish(t('customers.refresh.selectAll'))}
      </Text>
    ),
    [allSelected]
  )

  useEffect(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.trim().length === 0) {
      setDialogState({
        pristine: true,
        loading: false,
        searchResults: []
      })
      return
    }
    const controller = new AbortController()
    const { signal } = controller
    handleSearchQuery(debouncedSearchTerm, signal)

    return () => controller.abort()
  }, [debouncedSearchTerm, handleSearchQuery])
  return (
    <Modal
      id="opportunity-import-modal"
      title={capitalizeIfEnglish(t('customers.importOpportunities'))}
      isOpen={modalOpen}
      onClose={closeModal}
      onPrimary={handlePrimaryAction}
      onSecondary={closeModal}
      dismissOnBackdrop={!buttonLoading}
      disablePrimaryButton={!someSelected}
      loadingPrimaryButton={buttonLoading}
      primaryBtnLabel={capitalizeIfEnglish(t('customers.import'))}
      secondaryBtnLabel={capitalizeIfEnglish(t('errors.modalCancel'))}
      size="medium"
      onThird={toggleAll}
      disableThirdButton={buttonLoading || searchResults.length === 0}
      thirdBtnContent={thirdBtnContent}
    >
      <div className="flex flex-col gap-6 justify-center items-center p-0.5 w-full">
        <SearchBox
          id="import-opportunity-popup-searchbox"
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          placeHolder={capitalizeIfEnglish(t('customers.importDialog.infoText'))}
          className="placeholder:text-[12px]"
          isDisabled={buttonLoading}
        />
        {loading && (
          <div className="mt-3">
            <Spinner id="import-popup-spinner" type={'primary'} size={19} speed={1} />
          </div>
        )}
        {!loading && !pristine && (
          <OpportunityImportList
            searchResults={searchResults}
            opportunityIds={opportunityIds}
            buttonLoading={buttonLoading}
            allSelected={allSelected}
            toggleAll={toggleAll}
            toggleRow={toggleRow}
          />
        )}
      </div>
    </Modal>
  )
}
export default OpportunityImportModal
